import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Heart, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import useWishlistCount from "@/hooks/useWishlistCount";

/**
 * WishlistWidget — floating counter for signed-in members showing "You're
 * watching N drops" with a jump-to-saved-list CTA.
 */

const DISMISS_KEY = "ff2d_wishlist_widget_dismissed_v1";

export default function WishlistWidget() {
  const { user } = useAuth();
  const count = useWishlistCount();
  const [dismissed, setDismissed] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");
    } catch { /* noop */ }
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
  };

  // Hide conditions
  if (!user?.is_member) return null;
  if (count <= 0) return null;
  if (dismissed) return null;
  // Suppress on any account/saved page where the list is already visible
  if (pathname.startsWith("/account")) return null;

  const label = count === 1 ? "1 drop" : `${count} drops`;

  return (
    <div
      className="fixed bottom-6 left-6 z-[70] animate-in slide-in-from-bottom-4 fade-in duration-500"
      data-testid="wishlist-widget"
      role="status"
    >
      <div className="flex items-center gap-2 bg-[var(--ff-primary)] text-white rounded-full pl-2 pr-1 py-1 shadow-lg border border-[var(--ff-primary-hover)] hover:shadow-xl transition-shadow">
        <Link
          to="/account?tab=saved"
          className="flex items-center gap-2 pl-2 pr-1 py-1.5 group"
          data-testid="wishlist-widget-cta"
        >
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/15 text-[var(--ff-accent-kraft)] group-hover:bg-white/25 transition-colors">
            <Heart size={14} className="fill-current" />
          </span>
          <span className="text-sm">
            You&apos;re watching{" "}
            <span className="font-semibold" data-testid="wishlist-widget-count">{label}</span>
          </span>
          <span className="text-xs text-[var(--ff-accent-kraft)] font-semibold pl-1 pr-2 opacity-80 group-hover:opacity-100 transition-opacity">
            View →
          </span>
        </Link>
        <button
          type="button"
          onClick={dismiss}
          className="ml-1 mr-1 w-6 h-6 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Dismiss wishlist widget"
          data-testid="wishlist-widget-dismiss"
        >
          <X size={12} />
        </button>
      </div>
    </div>
  );
}
